import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosConfig';

const KanbanBoard = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'backlog',
    priority: 'medium',
    projectId: '',
    assignedTo: '',
    dueDate: new Date().toISOString().split('T')[0]
  });
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);

  // Carregar projetos e tarefas
  useEffect(() => {
    setLoading(true);
    
    // Simulando carregamento
    setTimeout(() => {
      // Dados estáticos para projetos
      const projectsData = [
        { id: 1, name: 'Melhoria do Painel de Conhecimento', description: 'Adicionar recursos de gestão de projetos ao painel de conhecimento' },
        { id: 2, name: 'Correção de Bugs de Autenticação', description: 'Corrigir problemas com o tratamento de erros 401' },
        { id: 3, name: 'Refatoração do Módulo de Relatórios', description: 'Melhorar a estrutura e performance dos relatórios' }
      ];
      
      // Dados estáticos para tarefas
      const tasksData = [
        { 
          id: 1, 
          title: 'Implementar sistema de abas no Dashboard', 
          description: 'Adicionar um sistema de abas para alternar entre a base de conhecimento e o Kanban',
          status: 'done',
          priority: 'high',
          projectId: 1,
          assignedTo: 'João Silva',
          dueDate: '2025-03-15'
        },
        { 
          id: 2, 
          title: 'Criar componente KanbanBoard', 
          description: 'Desenvolver um componente para visualização e gerenciamento de tarefas em formato Kanban',
          status: 'in-progress',
          priority: 'high',
          projectId: 1,
          assignedTo: 'Maria Oliveira',
          dueDate: '2025-03-20'
        },
        { 
          id: 3, 
          title: 'Melhorar tratamento de erro 401', 
          description: 'Implementar melhor feedback ao usuário quando ocorrer um erro de autenticação',
          status: 'done',
          priority: 'critical',
          projectId: 2,
          assignedTo: 'Carlos Santos',
          dueDate: '2025-03-10'
        },
        { 
          id: 4, 
          title: 'Adicionar redirecionamento automático após expiração do token', 
          description: 'Redirecionar o usuário para a página de login quando o token expirar',
          status: 'backlog',
          priority: 'medium',
          projectId: 2,
          assignedTo: 'Ana Pereira',
          dueDate: '2025-03-25'
        },
        { 
          id: 5, 
          title: 'Otimizar queries do relatório mensal', 
          description: 'Melhorar a performance das consultas ao banco de dados para o relatório mensal',
          status: 'backlog',
          priority: 'medium',
          projectId: 3,
          assignedTo: 'Pedro Costa',
          dueDate: '2025-04-05'
        }
      ];
      
      setProjects(projectsData);
      setTasks(tasksData);
      setLoading(false);
    }, 1000);
  }, []);

  // Filtrar tarefas com base no projeto selecionado
  const filteredTasks = tasks.filter(task => 
    selectedProject === 'all' || task.projectId === parseInt(selectedProject)
  );

  // Agrupar tarefas por status
  const backlogTasks = filteredTasks.filter(task => task.status === 'backlog');
  const inProgressTasks = filteredTasks.filter(task => task.status === 'in-progress');
  const inReviewTasks = filteredTasks.filter(task => task.status === 'in-review');
  const doneTasks = filteredTasks.filter(task => task.status === 'done');

  // Função para atualizar o status de uma tarefa (drag and drop)
  const updateTaskStatus = (taskId, newStatus) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, status: newStatus } : task
    ));
    toast.success('Status da tarefa atualizado com sucesso!');
  };

  // Função para adicionar nova tarefa
  const handleAddTask = () => {
    // Validação simples
    if (!newTask.title || !newTask.projectId) {
      toast.error('Por favor, preencha pelo menos o título e selecione um projeto');
      return;
    }

    // Simulando adição de nova tarefa
    const newTaskId = tasks.length + 1;
    const taskToAdd = {
      ...newTask,
      id: newTaskId,
      projectId: parseInt(newTask.projectId)
    };

    setTasks([...tasks, taskToAdd]);
    setShowAddTaskModal(false);
    setNewTask({
      title: '',
      description: '',
      status: 'backlog',
      priority: 'medium',
      projectId: '',
      assignedTo: '',
      dueDate: new Date().toISOString().split('T')[0]
    });
    toast.success('Tarefa adicionada com sucesso!');
  };

  // Renderizar o componente do quadro Kanban
  return (
    <div>
      <div className="d-flex justify-content-between mb-4 align-items-center">
        <div>
          <select
            className="form-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="all">Todos os Projetos</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddTaskModal(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M12 5l0 14"></path>
            <path d="M5 12l14 0"></path>
          </svg>
          Nova Tarefa
        </button>
      </div>

      {loading ? (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="mt-2">Carregando quadro Kanban...</div>
        </div>
      ) : (
        <div className="row g-3">
          {/* Coluna Backlog */}
          <div className="col-md-3">
            <div className="card">
              <div className="card-status-top bg-secondary"></div>
              <div className="card-header bg-light">
                <h3 className="card-title">Backlog</h3>
                <span className="badge bg-secondary ms-2">{backlogTasks.length}</span>
              </div>
              <div className="card-body p-2" style={{ minHeight: '300px' }}>
                {backlogTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    projects={projects}
                    onStatusChange={updateTaskStatus}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Coluna Em Andamento */}
          <div className="col-md-3">
            <div className="card">
              <div className="card-status-top bg-primary"></div>
              <div className="card-header bg-light">
                <h3 className="card-title">Em Andamento</h3>
                <span className="badge bg-primary ms-2">{inProgressTasks.length}</span>
              </div>
              <div className="card-body p-2" style={{ minHeight: '300px' }}>
                {inProgressTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    projects={projects}
                    onStatusChange={updateTaskStatus}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Coluna Em Revisão */}
          <div className="col-md-3">
            <div className="card">
              <div className="card-status-top bg-info"></div>
              <div className="card-header bg-light">
                <h3 className="card-title">Em Revisão</h3>
                <span className="badge bg-info ms-2">{inReviewTasks.length}</span>
              </div>
              <div className="card-body p-2" style={{ minHeight: '300px' }}>
                {inReviewTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    projects={projects}
                    onStatusChange={updateTaskStatus}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Coluna Concluído */}
          <div className="col-md-3">
            <div className="card">
              <div className="card-status-top bg-success"></div>
              <div className="card-header bg-light">
                <h3 className="card-title">Concluído</h3>
                <span className="badge bg-success ms-2">{doneTasks.length}</span>
              </div>
              <div className="card-body p-2" style={{ minHeight: '300px' }}>
                {doneTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    projects={projects}
                    onStatusChange={updateTaskStatus}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para adicionar nova tarefa */}
      {showAddTaskModal && (
        <div className="modal modal-blur show" tabIndex="-1" role="dialog" style={{ display: 'block' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nova Tarefa</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddTaskModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Título</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newTask.title}
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Descrição</label>
                  <textarea 
                    className="form-control" 
                    rows="3"
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  ></textarea>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Projeto</label>
                    <select 
                      className="form-select"
                      value={newTask.projectId}
                      onChange={(e) => setNewTask({...newTask, projectId: e.target.value})}
                    >
                      <option value="">Selecione um projeto</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Prioridade</label>
                    <select 
                      className="form-select"
                      value={newTask.priority}
                      onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                    >
                      <option value="low">Baixa</option>
                      <option value="medium">Média</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Atribuído a</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={newTask.assignedTo}
                      onChange={(e) => setNewTask({...newTask, assignedTo: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Data de Entrega</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddTaskModal(false)}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={handleAddTask}>Adicionar Tarefa</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para o cartão de tarefa
const TaskCard = ({ task, projects, onStatusChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Encontrar o projeto correspondente à tarefa
  const taskProject = projects.find(p => p.id === task.projectId);
  
  // Obter cor com base na prioridade
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'secondary';
      case 'medium': return 'info';
      case 'high': return 'warning';
      case 'critical': return 'danger';
      default: return 'secondary';
    }
  };
  
  // Obter texto da prioridade
  const getPriorityText = (priority) => {
    switch (priority) {
      case 'low': return 'Baixa';
      case 'medium': return 'Média';
      case 'high': return 'Alta';
      case 'critical': return 'Crítica';
      default: return 'Média';
    }
  };
  
  return (
    <div className="card mb-2">
      <div className="card-body p-3">
        <div className="d-flex align-items-center mb-2">
          <span className={`badge bg-${getPriorityColor(task.priority)} me-2`}>
            {getPriorityText(task.priority)}
          </span>
          <div className="ms-auto dropdown">
            <button 
              className="btn btn-sm btn-ghost-secondary"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-dots-vertical" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                <path d="M12 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                <path d="M12 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
              </svg>
            </button>
            {showDropdown && (
              <div className="dropdown-menu dropdown-menu-end show">
                <span className="dropdown-header">Mover para</span>
                <button 
                  className="dropdown-item"
                  onClick={() => {
                    onStatusChange(task.id, 'backlog');
                    setShowDropdown(false);
                  }}
                >
                  Backlog
                </button>
                <button 
                  className="dropdown-item"
                  onClick={() => {
                    onStatusChange(task.id, 'in-progress');
                    setShowDropdown(false);
                  }}
                >
                  Em Andamento
                </button>
                <button 
                  className="dropdown-item"
                  onClick={() => {
                    onStatusChange(task.id, 'in-review');
                    setShowDropdown(false);
                  }}
                >
                  Em Revisão
                </button>
                <button 
                  className="dropdown-item"
                  onClick={() => {
                    onStatusChange(task.id, 'done');
                    setShowDropdown(false);
                  }}
                >
                  Concluído
                </button>
              </div>
            )}
          </div>
        </div>
        <h4 className="card-title mb-1">{task.title}</h4>
        <p className="text-muted small mb-2">{task.description}</p>
        <div className="d-flex align-items-center mt-2">
          <div className="avatar bg-primary-lt">{task.assignedTo.charAt(0).toUpperCase()}</div>
          <div className="ms-2">
            <small className="text-muted d-block">{taskProject?.name}</small>
            <small className="text-muted">Vencimento: {task.dueDate.split('-').reverse().join('/')}</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanBoard; 